import {HOTB} from "../HOTB";
import {ExtendedGameMode} from "../server/ExtendedGameMode";
import {StageLayer} from "@nolimitcity/slot-launcher/bin/StageLayer";
import {BasicBitmapCountUp} from "../components/BasicBitmapCountUp";
import {GameAssets} from "../GameAssets";
import {gsap, TimelineLite} from "gsap";
import {HOTBData, IWinRatioConfig, WinTypes} from "../server/HOTBData";
import {GameUtils} from "../utils/GameUtils";
import {ExtendedGameModel} from "../server/ExtendedGameModel";
import {GameSounds} from "../sound/GameSounds";

export class TotalWinView extends PIXI.Container implements StageLayer {
    public isPlayedCountUpEnds: boolean = false;
    private _splashImage: PIXI.Sprite;
    private _totalWinCountUp: BasicBitmapCountUp;
    private _totalWinAmount: number;
    private _mainTl: gsap.core.Timeline;
    private _waysTextContainer: PIXI.Container;
    private _isWinShown: boolean = false;

    constructor() {
        super();
        this.name = "TotalWinView";
        this.init();
    }

    public getTotalWinTimeline(totalWin: number): gsap.core.Timeline {
        this.isPlayedCountUpEnds = false;
        this._isWinShown = false;

        this._mainTl = gsap.timeline();

        if (this._mainTl && this._mainTl.isActive()) {
            this._mainTl.pause();
            this._mainTl.kill();
        }

        this._totalWinAmount = totalWin;

        const gameModel: ExtendedGameModel = HOTB.model,
            hotbData: HOTBData = gameModel.data,
            winRatioConfig: IWinRatioConfig = gameModel.winRatioConfig;

        if ((hotbData.mode.includes(ExtendedGameMode.FREESPIN)) && this._totalWinAmount === 0) {
            return this._mainTl;
        }

        if (GameUtils.isSingleWinBelowStake()) {
            this.toggleItems(false);
            return this._mainTl;
        }

        this._mainTl.add((): void => {
            this._waysTextContainer.alpha = 1;
            this.toggleItems(true);
        }, 0);

        if (winRatioConfig.duration === 0) {
            this._mainTl.add([
                gsap.fromTo(this._totalWinCountUp.scale, {
                    x: 0,
                    y: 0
                }, {
                    x: 1,
                    y: 1,
                    duration: 0.1
                }),
                this._totalWinCountUp.getCountAnimation(totalWin, totalWin, winRatioConfig.duration, "power1.easeOut")
            ]);

            this._mainTl.add((): void => {
                this._splashImage.alpha = 1;
                this._totalWinCountUp.alpha = 1;
                this.playNormalWinSound(winRatioConfig.type);
            }, 0);
        } else {
            this._splashImage.alpha = 0;
            this._totalWinCountUp.alpha = 0;

            this._mainTl.to([this._splashImage, this._totalWinCountUp], {
                duration: 0.2,
                alpha: 1,
                ease: "power3.out"
            }, 0);

            this._mainTl.add([
                gsap.fromTo(this._totalWinCountUp.scale, {
                    x: 0,
                    y: 0
                }, {
                    x: 1,
                    y: 1,
                    duration: 0.1
                }),
                this._totalWinCountUp.getCountAnimation(0, totalWin, winRatioConfig.duration),
                (): void => {
                    HOTB.soundPlayer.playSound(GameSounds.WIN_LARGE_START);
                }
            ], 0);

            this._mainTl.add((): void => {
                this._totalWinCountUp.stopAt(totalWin);
                HOTB.soundPlayer.playSound(GameSounds.WIN_LARGE_END);
                this.showSplashImage();
            });
        }

        if(!(hotbData.freespinsLeft === 0 && gameModel.gameData.numberOfFreespinsPlayed > 0)){
            HOTB.keypadPlugin.setWin(HOTB.model.gameData.accumulatedRoundWin, true, GameUtils.isWinBelowStake());
        }

        this._mainTl.to([this._splashImage, this._totalWinCountUp, this._waysTextContainer], {
            duration: 0.2,
            alpha: 0,
            ease: "power3.out"
        }, "+=1.2");

        this._mainTl.add(()=> {
            this._isWinShown = true;
        },"<");

        this._mainTl.add((): void => {
            this.toggleItems(false);
            this._splashImage.alpha = 1;
            this._totalWinCountUp.alpha = 1;
        });

        return this._mainTl;
    }

    public hardAbort(): void {
        if (this._mainTl) {
            this._mainTl.pause();
            this._mainTl.kill();
            if(!(HOTB.model.data.freespinsLeft === 0 && HOTB.model.gameData.numberOfFreespinsPlayed > 0)){
                HOTB.keypadPlugin.setWin(HOTB.model.gameData.accumulatedRoundWin, true, GameUtils.isWinBelowStake());
            }
            if(!this._isWinShown){
                this._splashImage.alpha = 1;
                this._totalWinCountUp.alpha = 1;
                this.toggleItems(true);
            }
            this._totalWinCountUp.position.set(360, 430);
            this._totalWinCountUp.stopAt(this._totalWinAmount);
            this.fadeOutWinPres(0.1, 0.1);
        }
    }

    private fadeOutWinPres(duration: number, delay: number): void {
        const tl: TimelineLite = new TimelineLite({
            delay: delay
        });

        tl.to([this._splashImage, this._totalWinCountUp], duration, {
            duration: duration,
            alpha: 0,
            ease: "power3.out"
        });

        tl.add((): void => {
            this.toggleItems(false);
        });
    }

    private init(): void {
        this.createSplashImage();
        this.createCountUp();

        this._waysTextContainer = this.addChild(new PIXI.Container());
        this._waysTextContainer.position.set(360, 500);

        const gameModel: ExtendedGameModel = HOTB.model;

        if (gameModel.isRestore) {
            HOTB.keypadPlugin.setWin(gameModel.initData.accumulatedRoundWin, true, GameUtils.isWinBelowStake());
        }
    }

    private createSplashImage(): void {
        this._splashImage = new PIXI.Sprite(HOTB.assetLoader.getTextures("winSplash")[0]);
        this._splashImage.anchor.set(0.5);
        this._splashImage.position.set(360, 430);
        this._splashImage.visible = false;
        this.addChild(this._splashImage);
    }

    private createCountUp(): void {
        this._totalWinCountUp = new BasicBitmapCountUp({
            fontName: GameAssets.BITMAP_FONT_WIN_COUNT_UP,
            fontSize: 38.5,
        }, 680, new PIXI.Point(0.5, 0.5));
        this._totalWinCountUp.position.set(360, 430);
        this._totalWinCountUp.visible = false;
        this.addChild(this._totalWinCountUp);
    }

    private toggleItems(showItems: boolean) {
        this._splashImage.visible = false;
        this._totalWinCountUp.visible = showItems;

        if (showItems) {
            this.createWaysText();

            if (HOTB.model.winRatioConfig.type === WinTypes.MEDIUM) {
                this.showSplashImage();
            }
        } else {
            this._waysTextContainer.removeChildren();
        }
    }

    private showSplashImage(): void {
        this._splashImage.visible = true;
    }

    private createWaysText(): void {
        const waysCount: number = HOTB.model.gameData.waysWonTotalRound,
            textName: string = HOTB.apiPlugin.translations.translate(waysCount > 1 ? "Ways" : "Way"),
            waysText: PIXI.Text = new PIXI.Text(waysCount + " " + textName, {
                fontName: "OpenSans-Bold",
                fontSize: 33,
                fill: "#ffcd60",
                fontWeight: "bold",
                dropShadow: true,
                dropShadowColor: "#000000",
                dropShadowBlur: 2,
                dropShadowDistance: 3.5,
                dropShadowAngle: Math.PI / 5,
                strokeColor: "#000000",
                strokeThickness: 5,
            });

        waysText.anchor.set(0.5, 0);
        waysText.alpha = 0.6;
        waysText.y = 12.5;

        if (waysCount) {
            this._waysTextContainer.addChild(waysText);
        }
    }

    private playNormalWinSound(winType: string): void {
        let soundName: string;

        switch (winType) {
            case WinTypes.TINY:
                soundName = GameSounds.WIN_TINY;
                break;
            case WinTypes.SMALL:
                soundName = GameSounds.WIN_SMALL;
                break;
            case WinTypes.MEDIUM:
                soundName = GameSounds.WIN_MEDIUM;
                break;
            default:
                soundName = "";
                break;
        }

        HOTB.soundPlayer.playSound(soundName);
    }
}